import { LANGS, PAGES, SITE_URL, USE_CASES, OG_LOCALE, CONTACTS, COMPANY, STORES } from "./content/shared";
import { DICTS } from "./i18n";
import DEMOS from "./content/demos";

// Every indexable path without language prefix.
export const ALL_PATHS = [
  ...Object.values(PAGES),
  ...USE_CASES.map((u) => `${PAGES.useCases}/${u.slug}`),
];

const keyForPath = (rest) => {
  if (rest.startsWith(`${PAGES.useCases}/`)) return { key: "useCase", slug: rest.split("/")[1] };
  const key = Object.keys(PAGES).find((k) => PAGES[k] === rest);
  return { key: key || "notFound" };
};

export const urlFor = (lang, rest) => `${SITE_URL}/${lang}/${rest ? `${rest}/` : ""}`;

export function getSeo(lang, rest) {
  const t = DICTS[lang] || DICTS.en;
  const { key, slug } = keyForPath(rest);
  let title, description;
  if (key === "useCase") {
    const uc = t.useCases.items[slug];
    if (!uc) return getSeo(lang, "__404");
    title = uc.metaTitle;
    description = uc.metaDesc;
  } else {
    [title, description] = t.meta[key] || t.meta.notFound;
  }
  const found = key !== "notFound";
  return {
    title,
    description,
    lang,
    found,
    canonical: found ? urlFor(lang, rest) : null,
    alternates: found ? LANGS.map((l) => ({ hreflang: l, href: urlFor(l, rest) })) : [],
    xDefault: found ? urlFor("en", rest) : null,
    ogLocale: OG_LOCALE[lang],
    jsonLd: found ? buildJsonLd(lang, key, slug, t) : null,
  };
}

function buildJsonLd(lang, key, slug, t) {
  const graph = [
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#org`,
      name: "ChatPlate",
      legalName: COMPANY.name,
      url: `${SITE_URL}/`,
      logo: `${SITE_URL}/images/icon.png`,
      email: CONTACTS.support,
      address: { "@type": "PostalAddress", streetAddress: COMPANY.street, addressLocality: "Sacramento", addressRegion: "CA", postalCode: "95825", addressCountry: "US" },
    },
  ];
  if (key === "home" || key === "download" || key === "product") {
    graph.push({
      "@type": "MobileApplication",
      name: "ChatPlate",
      operatingSystem: "iOS, Android",
      applicationCategory: "CommunicationApplication",
      installUrl: [STORES.ios, STORES.android],
      publisher: { "@id": `${SITE_URL}/#org` },
      inLanguage: lang,
    });
  }
  if (key === "help") {
    graph.push({
      "@type": "FAQPage",
      inLanguage: lang,
      mainEntity: [...t.help.cats, DEMOS[lang].help].flatMap((c) => c.items).map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
    });
  }
  return { "@context": "https://schema.org", "@graph": graph };
}

export function sitemapXml(lastmod) {
  const urls = ALL_PATHS.flatMap((rest) =>
    LANGS.map((l) => {
      const alts = LANGS.map((a) => `    <xhtml:link rel="alternate" hreflang="${a}" href="${urlFor(a, rest)}"/>`).join("\n");
      return `  <url>\n    <loc>${urlFor(l, rest)}</loc>\n    <lastmod>${lastmod}</lastmod>\n${alts}\n    <xhtml:link rel="alternate" hreflang="x-default" href="${urlFor("en", rest)}"/>\n  </url>`;
    })
  );
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:xhtml="http://www.w3.org/1999/xhtml">\n${urls.join("\n")}\n</urlset>\n`;
}

const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

// Head markup used by the build-time prerender.
export function headHtml(seo) {
  const tags = [
    `<title>${esc(seo.title)}</title>`,
    `<meta name="description" content="${esc(seo.description)}" />`,
    `<meta name="robots" content="${seo.found ? "index, follow, max-image-preview:large" : "noindex"}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="ChatPlate" />`,
    `<meta property="og:title" content="${esc(seo.title)}" />`,
    `<meta property="og:description" content="${esc(seo.description)}" />`,
    `<meta property="og:locale" content="${seo.ogLocale}" />`,
    `<meta property="og:image" content="${SITE_URL}/images/og-chatplate.png" />`,
    `<meta property="og:image:width" content="1200" />`,
    `<meta property="og:image:height" content="630" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${esc(seo.title)}" />`,
    `<meta name="twitter:description" content="${esc(seo.description)}" />`,
    `<meta name="twitter:image" content="${SITE_URL}/images/og-chatplate.png" />`,
  ];
  if (seo.canonical) {
    tags.push(`<link rel="canonical" href="${seo.canonical}" />`, `<meta property="og:url" content="${seo.canonical}" />`);
    seo.alternates.forEach((a) => tags.push(`<link rel="alternate" hreflang="${a.hreflang}" href="${a.href}" />`));
    tags.push(`<link rel="alternate" hreflang="x-default" href="${seo.xDefault}" />`);
    tags.push(`<script type="application/ld+json">${JSON.stringify(seo.jsonLd)}</script>`);
  }
  return tags.map((x) => `        ${x}`).join("\n");
}
